/**
 * Walk-session OTP modal — the walker sends a code to the owner (SMS) and enters
 * what the owner reads back to start / finish the walk. The end variant also
 * offers an "Owner unavailable" override (audited completion without a code).
 */
import { useEffect, useState } from 'react';
import { Loader2, MessageSquareText, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { form_error_message } from '@/lib/forms';
import { use_send_walk_otp, use_verify_walk_otp, use_override_walk_end } from '@/features/bookings/hooks';
import type { Booking } from '@/features/bookings/types';

const field =
  'flex h-11 w-full rounded-input border-2 border-input bg-card px-3 py-2 text-sm shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:outline-none focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15';

function Shell({ title, on_close, children }: { title: string; on_close: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" onClick={on_close}>
      <div className="my-12 w-full max-w-md rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="font-heading text-lg font-bold">{title}</h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-4 p-5">{children}</div>
      </div>
    </div>
  );
}

export function WalkOtpModal({
  booking,
  kind,
  on_close,
}: {
  booking: Booking;
  kind: 'start' | 'end';
  on_close: () => void;
}) {
  const send_m = use_send_walk_otp();
  const verify_m = use_verify_walk_otp();
  const override_m = use_override_walk_end();

  const [code, set_code] = useState('');
  const [sent, set_sent] = useState(false);
  const [cooldown, set_cooldown] = useState(0);
  const [error, set_error] = useState<string | null>(null);
  const [overriding, set_overriding] = useState(false);
  const [reason, set_reason] = useState('');

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => set_cooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const contact = booking.contact_phone ?? 'the owner';
  const verb = kind === 'start' ? 'start' : 'finish';

  async function send() {
    set_error(null);
    try {
      const r = await send_m.mutateAsync({ id: booking.id, kind });
      set_sent(true);
      set_cooldown(r.cooldown_seconds);
      if (r.debug_otp_code) set_code(r.debug_otp_code); // dev auto-fill (stub provider)
    } catch (err) {
      set_error(form_error_message(err, 'Could not send the code.'));
    }
  }

  async function verify() {
    set_error(null);
    try {
      await verify_m.mutateAsync({ id: booking.id, kind, code: code.trim() });
      on_close();
    } catch (err) {
      set_error(form_error_message(err, 'Could not verify the code.'));
    }
  }

  async function override() {
    set_error(null);
    if (!reason.trim()) {
      set_error('Add a short reason.');
      return;
    }
    try {
      await override_m.mutateAsync({ id: booking.id, reason: reason.trim() });
      on_close();
    } catch (err) {
      set_error(form_error_message(err, 'Could not complete the walk.'));
    }
  }

  return (
    <Shell title={kind === 'start' ? 'Start walk' : 'Finish walk'} on_close={on_close}>
      {overriding ? (
        <>
          <p className="text-sm text-muted-foreground">
            Complete this walk without the owner's code. This is recorded for review — use it only when the
            owner is genuinely unreachable at drop-off.
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="override-reason">Reason</Label>
            <textarea
              id="override-reason"
              className={`${field} h-20 resize-y py-2`}
              maxLength={500}
              value={reason}
              onChange={(e) => set_reason(e.target.value)}
              placeholder="e.g. Owner not home, left dog with the doorman"
              data-testid="walk-otp-override-reason"
            />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="walk-otp-error">{error}</p>}
          <div className="flex justify-between gap-2">
            <Button variant="ghost" onClick={() => { set_overriding(false); set_error(null); }}>Back</Button>
            <Button variant="destructive" onClick={override} disabled={override_m.isPending} data-testid="walk-otp-override-submit">
              {override_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Complete anyway
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            We'll text a 6-digit code to <span className="font-medium text-foreground">{contact}</span>. Ask them
            to read it back, then enter it to {verb} the walk.
          </p>

          {!sent ? (
            <Button variant="gradient" onClick={send} disabled={send_m.isPending} data-testid="walk-otp-send">
              {send_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareText className="h-4 w-4" />}
              Send code to owner
            </Button>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="walk-otp-code">6-digit code from the owner</Label>
                <input
                  id="walk-otp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className={`${field} tracking-[0.4em]`}
                  value={code}
                  onChange={(e) => set_code(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••••"
                  data-testid="walk-otp-code"
                />
              </div>
              <button
                type="button"
                className="self-start text-xs font-semibold text-primary disabled:text-muted-foreground"
                onClick={send}
                disabled={cooldown > 0 || send_m.isPending}
                data-testid="walk-otp-resend"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          )}

          {error && <p className="text-sm text-destructive" data-testid="walk-otp-error">{error}</p>}

          <div className="flex items-center justify-between gap-2">
            {kind === 'end' ? (
              <button
                type="button"
                className="text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => { set_overriding(true); set_error(null); }}
                data-testid="walk-otp-owner-unavailable"
              >
                Owner unavailable?
              </button>
            ) : (
              <span />
            )}
            <Button
              variant="gradient"
              onClick={verify}
              disabled={!sent || code.length !== 6 || verify_m.isPending}
              data-testid="walk-otp-verify"
            >
              {verify_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {kind === 'start' ? 'Verify & start' : 'Verify & finish'}
            </Button>
          </div>
        </>
      )}
    </Shell>
  );
}
